import { useEffect, useState } from "react";
import { Timer } from "lucide-react";
import { CAMPAIGN, getNextDraw } from "@/config/campaign";
import { getPhase, getCountdownTarget } from "@/lib/phase";

// Countdown auf die nächste echte Ziehung (bzw. Pre-Launch auf den Start).
// Kein Fake-Timer: Ziel kommt aus dem fixen Ziehungsplan.
const resolveTarget = () => {
  const phase = getPhase();
  if (phase === "prelaunch") {
    return { ts: getCountdownTarget("prelaunch"), label: `Start am ${CAMPAIGN.launchLabel.replace(" 2026", "")}`, drawLabel: CAMPAIGN.launchLabel };
  }
  if (phase === "closed") {
    const finalDraw = getNextDraw();
    if (finalDraw) return { ts: new Date(finalDraw.at).getTime(), label: "Finale Ziehung in", drawLabel: finalDraw.label };
    return null;
  }
  const nxt = getNextDraw();
  if (!nxt) return null;
  return { ts: new Date(nxt.at).getTime(), label: "Nächste Gewinnerziehung in", drawLabel: nxt.label };
};

const getRemaining = (target) => {
  const diff = Math.max(0, target - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const DigitBox = ({ value, label, testId, tone }) => (
  <div className="flex flex-col items-center gap-1.5">
    <div
      className={`flex h-14 w-14 items-center justify-center rounded-[14px] border font-display text-2xl font-extrabold tabular-nums sm:h-16 sm:w-16 sm:text-3xl ${
        tone === "dark"
          ? "border-gold/30 bg-coal text-offwhite"
          : "border-line bg-white text-ink shadow-sm"
      }`}
      data-testid={testId}
    >
      {String(value).padStart(2, "0")}
    </div>
    <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] sm:text-xs ${tone === "dark" ? "text-offwhite/60" : "text-mutedlight"}`}>
      {label}
    </span>
  </div>
);

export const Countdown = ({ centered = false, tone = "light" }) => {
  const [cfg, setCfg] = useState(resolveTarget);
  const [t, setT] = useState(() => (cfg ? getRemaining(cfg.ts) : null));

  useEffect(() => {
    const id = setInterval(() => {
      const c = resolveTarget();
      setCfg(c);
      setT(c ? getRemaining(c.ts) : null);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (!cfg || !t) {
    return (
      <div className={centered ? "flex flex-col items-center" : ""} data-testid="countdown-timer">
        <p className={`text-sm font-semibold ${tone === "dark" ? "text-offwhite/70" : "text-mutedlight"}`} data-testid="countdown-label">
          Alle Ziehungen sind abgeschlossen.
        </p>
      </div>
    );
  }

  return (
    <div className={centered ? "flex flex-col items-center" : ""} data-testid="countdown-timer">
      <p
        className={`mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark`}
        data-testid="countdown-label"
      >
        <Timer className="h-4 w-4" strokeWidth={2.5} />
        {cfg.label}
      </p>
      <div className="flex items-start gap-2.5 sm:gap-3">
        <DigitBox value={t.days} label="Tage" testId="countdown-days" tone={tone} />
        <span className="pt-3 font-display text-2xl font-extrabold text-gold-dark sm:pt-3.5" aria-hidden="true">:</span>
        <DigitBox value={t.hours} label="Std." testId="countdown-hours" tone={tone} />
        <span className="pt-3 font-display text-2xl font-extrabold text-gold-dark sm:pt-3.5" aria-hidden="true">:</span>
        <DigitBox value={t.minutes} label="Min." testId="countdown-minutes" tone={tone} />
        <span className="pt-3 font-display text-2xl font-extrabold text-gold-dark sm:pt-3.5" aria-hidden="true">:</span>
        <DigitBox value={t.seconds} label="Sek." testId="countdown-seconds" tone={tone} />
      </div>
      <p className={`mt-3 text-xs ${tone === "dark" ? "text-offwhite/60" : "text-mutedlight"}`} data-testid="countdown-deadline">
        Nächste Ziehung: {cfg.drawLabel} · Teilnahmeschluss: {CAMPAIGN.deadlineLabel}
      </p>
    </div>
  );
};
